'use client'

import 'swiper/css'

import { useFormatter, useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import ChevronRightIcon from '@/assets/icons/chevron-right.svg'
import { formatTaskHoursLeft } from '@/features/tasks/formatTaskHoursLeft'
import { getTaskCardCtaLabelKey } from '@/features/tasks/taskCardCta'

import { ComingSoonTaskCard } from '../ComingSoonTaskCard'
import type { TaskCardDemoRow } from '../TasksTaskCards/tasksTaskCards.constants'
import S from './TasksComingSoon.module.scss'

export type TasksComingSoonProps = {
  rows: readonly TaskCardDemoRow[]
}

export default function TasksComingSoon({ rows }: TasksComingSoonProps) {
  const t = useTranslations('tasksPage.comingSoon')
  const tCard = useTranslations('tasksPage.taskCard')
  const tTime = useTranslations('tasksPage.activeSection')
  const format = useFormatter()

  const [swiperApi, setSwiperApi] = useState<SwiperType | null>(null)
  const [navLocked, setNavLocked] = useState({ prev: true, next: true })

  const syncNav = useCallback((s: SwiperType) => {
    setNavLocked({ prev: s.isBeginning, next: s.isEnd })
  }, [])

  const visibleRows = useMemo(() => rows, [rows])

  useEffect(() => {
    if (!swiperApi) {
      return
    }
    requestAnimationFrame(() => {
      swiperApi.update()
      syncNav(swiperApi)
    })
  }, [swiperApi, syncNav, visibleRows.length])

  if (visibleRows.length === 0) {
    return null
  }

  return (
    <section className={S.section} aria-label={t('sectionAriaLabel')}>
      <div className={S.carouselHeader}>
        <h2 className={S.carouselTitle}>{t('sectionTitle')}</h2>
        <div className={S.carouselNav} role="group">
          <button
            type="button"
            className={S.carouselNavBtn}
            disabled={navLocked.prev}
            aria-label={t('navPrevAria')}
            onClick={() => {
              swiperApi?.slidePrev()
            }}
          >
            <ChevronRightIcon className={S.carouselNavIconPrev} aria-hidden />
          </button>
          <button
            type="button"
            className={S.carouselNavBtn}
            disabled={navLocked.next}
            aria-label={t('navNextAria')}
            onClick={() => {
              swiperApi?.slideNext()
            }}
          >
            <ChevronRightIcon className={S.carouselNavIconNext} aria-hidden />
          </button>
        </div>
      </div>
      <Swiper
        className={S.carouselSwiper}
        slidesPerView={1}
        spaceBetween={12}
        watchOverflow
        observer
        observeParents
        breakpoints={{
          769: { slidesPerView: 1.45, spaceBetween: 14 },
          900: { slidesPerView: 2.65, spaceBetween: 20 },
          1200: { slidesPerView: 3.35, spaceBetween: 22 },
          1440: { slidesPerView: 4, spaceBetween: 24 }
        }}
        onSwiper={s => {
          setSwiperApi(s)
          syncNav(s)
        }}
        onSlideChange={syncNav}
        onResize={syncNav}
        onBreakpoint={syncNav}
      >
        {visibleRows.map(row => {
          const title = row.apiTitle ?? tCard('fallbackTitle')
          const description = row.apiDescription ?? tCard('apiTaskDescription')

          return (
            <SwiperSlide key={row.id} className={S.carouselSlide}>
              <div className={S.carouselSlideInner}>
                <ComingSoonTaskCard
                  coverSrc={row.coverUrl}
                  brandName={row.apiBrandName ?? tCard('fallbackBrand')}
                  title={title}
                  description={description}
                  pointsValue={format.number(row.rewardPoints)}
                  timeLeftLabel={formatTaskHoursLeft(row.hoursLeft, tTime)}
                  badgeLabel={t('badge')}
                  notifyAriaLabel={t('notifyAriaLabel')}
                  ctaVariant={row.cta}
                  ctaLabel={tCard(getTaskCardCtaLabelKey(row.cta))}
                />
              </div>
            </SwiperSlide>
          )
        })}
      </Swiper>
    </section>
  )
}
